import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';

import { Arrayable } from '../../../helper/array';
import { toOrderByInput } from '../../../helper/sort';
import SortBy from '../../../models/SortBy';
import useFetchPeople from '../../users/services/useFetchPeople';
import BookingStaff, { BookingStaffFilterInput, BookingStaffOrderByInput } from '../models/BookingStaff';
import { GET_BOOKING_STAFF_MEMBERS } from './query';

interface QueryData {
  bookingStaffs: BookingStaff[];
}

interface QueryVars {
  orderBy?: Arrayable<BookingStaffOrderByInput>;
  where?: BookingStaffFilterInput;
}

/**
 * Fetch all staff members of the given booking
 */
function useFetchBookingStaffMembers() {
  const { fetchPeople } = useFetchPeople();

  const [execute, { loading, error, data, refetch, called }] = useLazyQuery<QueryData, QueryVars>(
    GET_BOOKING_STAFF_MEMBERS,
    {
      notifyOnNetworkStatusChange: true,
    },
  );

  const [staffMembers, setStaffMembers] = useState<BookingStaff[]>([]);

  const formatData = async (data?: BookingStaff[]): Promise<BookingStaff[]> => {
    const people = await fetchPeople((data ?? []).map((staffMember) => staffMember.externalUserId));

    return (data ?? []).map((staffMember) => {
      return {
        ...staffMember,
        user: people.find((person) => person._id === staffMember.externalUserId),
      };
    });
  };

  const fetchBookingStaffMembers = async (bookingId: string, sortBy?: SortBy) => {
    const variables: QueryVars = {
      orderBy: toOrderByInput(sortBy),
      where: {
        booking: {
          _id: { _eq: bookingId },
        },
      },
    };

    const response = called
      ? await refetch(variables)
      : await execute({
          variables,
        });

    const items = await formatData(response.data?.bookingStaffs);
    setStaffMembers(items);

    return items;
  };

  return { fetchBookingStaffMembers, loading, error, staffMembers };
}

export default useFetchBookingStaffMembers;
